import { useMutation } from "@tanstack/react-query";
import { useRef } from "react";

import { useNotifications } from "~/shared/hooks/use-notifications.ts";
import { api } from "~/shared/services/api/fetch-client.ts";
import type { ApiError } from "~/shared/types/api-types.ts";

import { mapFtlTenderErrorToUserMessage } from "./use-ftl-tender-error-mapper.ts";

async function tenderQuote(quoteId: number): Promise<void> {
	await api.put(`ftl-quote/${quoteId}/tender`, {
		method: "PUT",
	});
}

type UseTenderOptions = {
	onSuccess: (quoteId: number) => void;
	onError: (error: ApiError) => void;
};

export function useTender(options?: UseTenderOptions) {
	const { showSuccess, showError } = useNotifications();
	const consecutiveFailureCountRef = useRef(0);

	options ??= {
		onSuccess: () => showSuccess(`Quote tendered successfully!`),
		onError: (error) => showError(error.error),
	};

	const { error, isPending, mutate } = useMutation({
		mutationFn: tenderQuote,
		onSuccess: (_, quoteId) => {
			consecutiveFailureCountRef.current = 0;
			options.onSuccess(quoteId);
		},
		onError: async (error) => {
			console.error("FTL tender request failed:", error);
			consecutiveFailureCountRef.current += 1;
			const normalizedError = await mapFtlTenderErrorToUserMessage(
				error,
				consecutiveFailureCountRef.current,
			);
			options.onError(normalizedError);
		},
	});

	return {
		error,
		isPending,
		tender: mutate,
	};
}
